import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { createRoomGeneral } from "../services/interview-api.services";
import { CandidatePublic } from "../models/candidate-public.model";
import { useLanguage } from "../providers/language.provider";
import {
    DEMO_SESSION_LIMIT,
    getDemoSessionsRemaining,
    recordDemoSession,
} from "../utils/demo-session.util";
import LOGO_SO from "../assets/images/logo-so-black.png";
import "./demo.page.scss";

type Step = "consent" | "profile" | "permissions";
type PermissionState = "idle" | "checking" | "ready" | "denied" | "missing" | "unsupported";
type DemoLanguage = "en" | "th";

const copy = {
    en: {
        eyebrow: "AI INTERVIEW DEMO",
        title: "Try a short AI interview",
        subtitle: "Answer three questions from our maintained general interview set. It takes about 5 minutes.",
        consentTitle: "Before you begin",
        consentBody: "This demo records and submits camera and microphone responses so the interview experience can work. Use a nickname—do not enter your full name, email, phone number, or resume.",
        consentCheck: "I understand and consent to camera and microphone recording for this demo.",
        continue: "Continue",
        profileTitle: "How should we address you?",
        nickname: "Nickname",
        nicknamePlaceholder: "e.g. May",
        language: "Interview language",
        thai: "ภาษาไทย",
        english: "English",
        permissionTitle: "Check your camera and microphone",
        permissionBody: "Your browser will ask for access. Both devices are required for this demo.",
        checkPermission: "Check camera and microphone",
        checking: "Waiting for browser permission…",
        ready: "Camera and microphone are ready.",
        denied: "Access was blocked. Allow camera and microphone in your browser settings, then retry.",
        missing: "A camera or microphone could not be found. Connect both devices and retry.",
        unsupported: "This browser or connection does not support camera and microphone recording. Try a current Chrome, Edge, or Safari browser over HTTPS.",
        retry: "Retry permission check",
        launch: "Launch demo interview",
        launching: "Preparing your demo…",
        launchError: "We could not prepare the demo interview. Please try again.",
        limit: `This browser can start up to ${DEMO_SESSION_LIMIT} demos in 24 hours.`,
        limitReached: "This browser has reached the demo limit. Please try again after 24 hours.",
        remaining: (count: number) => `${count} demo ${count === 1 ? "session" : "sessions"} remaining`,
        back: "Back",
    },
    th: {
        eyebrow: "ทดลองสัมภาษณ์ด้วย AI",
        title: "ทดลองสัมภาษณ์สั้น ๆ กับ AI",
        subtitle: "ตอบคำถาม 3 ข้อจากชุดคำถามสัมภาษณ์ทั่วไป ใช้เวลาประมาณ 5 นาที",
        consentTitle: "ก่อนเริ่มต้น",
        consentBody: "เดโมนี้จะบันทึกและส่งภาพจากกล้องและเสียง เพื่อให้ระบบสัมภาษณ์ทำงาน กรุณาใช้ชื่อเล่น และไม่กรอกชื่อจริง อีเมล เบอร์โทร หรือเรซูเม่",
        consentCheck: "ฉันเข้าใจและยินยอมให้บันทึกภาพและเสียงสำหรับเดโมนี้",
        continue: "ดำเนินการต่อ",
        profileTitle: "ให้เราเรียกคุณว่าอะไร?",
        nickname: "ชื่อเล่น",
        nicknamePlaceholder: "เช่น เมย์",
        language: "ภาษาที่ใช้สัมภาษณ์",
        thai: "ภาษาไทย",
        english: "English",
        permissionTitle: "ตรวจสอบกล้องและไมโครโฟน",
        permissionBody: "เบราว์เซอร์จะขอสิทธิ์ใช้งาน เดโมนี้จำเป็นต้องใช้ทั้งกล้องและไมโครโฟน",
        checkPermission: "ตรวจสอบกล้องและไมโครโฟน",
        checking: "กำลังรอการอนุญาตจากเบราว์เซอร์…",
        ready: "กล้องและไมโครโฟนพร้อมใช้งาน",
        denied: "สิทธิ์ถูกปฏิเสธ กรุณาอนุญาตกล้องและไมโครโฟนในการตั้งค่าเบราว์เซอร์ แล้วลองใหม่",
        missing: "ไม่พบกล้องหรือไมโครโฟน กรุณาเชื่อมต่ออุปกรณ์ทั้งสองแล้วลองใหม่",
        unsupported: "เบราว์เซอร์หรือการเชื่อมต่อนี้ไม่รองรับการบันทึก กรุณาใช้ Chrome, Edge หรือ Safari เวอร์ชันล่าสุดผ่าน HTTPS",
        retry: "ลองตรวจสอบอีกครั้ง",
        launch: "เริ่มทดลองสัมภาษณ์",
        launching: "กำลังเตรียมเดโม…",
        launchError: "ไม่สามารถเตรียมเดโมได้ กรุณาลองใหม่",
        limit: `เบราว์เซอร์นี้เริ่มเดโมได้สูงสุด ${DEMO_SESSION_LIMIT} ครั้งใน 24 ชั่วโมง`,
        limitReached: "เบราว์เซอร์นี้ใช้เดโมครบจำนวนแล้ว กรุณาลองใหม่หลัง 24 ชั่วโมง",
        remaining: (count: number) => `เหลือเดโม ${count} ครั้ง`,
        back: "ย้อนกลับ",
    },
};

const DemoPage = () => {
    const navigate = useNavigate();
    const { switchLanguage } = useLanguage();
    const [step, setStep] = useState<Step>("consent");
    const [language, setLanguage] = useState<DemoLanguage>("th");
    const [consented, setConsented] = useState(false);
    const [nickname, setNickname] = useState("");
    const [permissionState, setPermissionState] = useState<PermissionState>("idle");
    const [isLaunching, setIsLaunching] = useState(false);
    const [launchError, setLaunchError] = useState("");
    const [remaining, setRemaining] = useState(() => getDemoSessionsRemaining(localStorage));
    const streamRef = useRef<MediaStream | null>(null);
    const text = copy[language];

    useEffect(() => () => {
        streamRef.current?.getTracks().forEach((track) => track.stop());
    }, []);

    const chooseLanguage = (value: DemoLanguage) => {
        setLanguage(value);
        switchLanguage(value);
    };

    const checkPermissions = async () => {
        setLaunchError("");
        if (
            !window.isSecureContext ||
            !navigator.mediaDevices?.getUserMedia ||
            typeof MediaRecorder === "undefined"
        ) {
            setPermissionState("unsupported");
            return;
        }

        setPermissionState("checking");
        streamRef.current?.getTracks().forEach((track) => track.stop());

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
            const hasCamera = stream.getVideoTracks().length > 0;
            const hasMic = stream.getAudioTracks().length > 0;
            stream.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
            setPermissionState(hasCamera && hasMic ? "ready" : "missing");
        } catch (error) {
            const name = error instanceof DOMException ? error.name : "";
            setPermissionState(
                name === "NotFoundError" || name === "OverconstrainedError" ? "missing" : "denied",
            );
        }
    };

    const launchDemo = async () => {
        const sessionsRemaining = getDemoSessionsRemaining(localStorage);
        setRemaining(sessionsRemaining);
        if (!sessionsRemaining || permissionState !== "ready") return;

        setIsLaunching(true);
        setLaunchError("");
        const sessionId = typeof crypto.randomUUID === "function"
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const now = new Date();
        const openUntil = new Date(now.getTime() + 2 * 60 * 60 * 1000);
        const candidate: CandidatePublic = {
            firstname: nickname.trim(),
            lastname: "",
            email: `demo.${sessionId}@interview.ai`,
            phone: "",
            status: "ACTIVE",
            jobTitle: "Demo",
            interview_start_date: now.toISOString(),
            interview_end_date: openUntil.toISOString(),
            jobExp: 0,
            resume: null,
            candidate_id: "",
        };

        try {
            const room = await createRoomGeneral(candidate);
            const result = room["result"];
            if (!result?.candidate_id || !result?.job_question_id) {
                throw new Error("Incomplete demo room response");
            }
            recordDemoSession(localStorage);
            navigate(
                `/${result.job_question_id}/${result.candidate_id}/${encodeURIComponent(candidate.email)}/interview-room?app_id=PUBLIC_DEMO`,
            );
        } catch {
            setLaunchError(text.launchError);
            setIsLaunching(false);
        }
    };

    const statusMessage = permissionState === "checking"
        ? text.checking
        : permissionState === "ready"
            ? text.ready
            : permissionState === "denied"
                ? text.denied
                : permissionState === "missing"
                    ? text.missing
                    : permissionState === "unsupported"
                        ? text.unsupported
                        : "";

    return (
        <main className="demo-page">
            <header className="demo-header">
                <img src={LOGO_SO} alt="ScoutOut" />
                <span>{text.eyebrow}</span>
            </header>

            <section className="demo-card">
                <div className="demo-progress" aria-label={`Step ${step === "consent" ? 1 : step === "profile" ? 2 : 3} of 3`}>
                    {[1, 2, 3].map((number) => (
                        <span
                            key={number}
                            className={number <= (step === "consent" ? 1 : step === "profile" ? 2 : 3) ? "active" : ""}
                        />
                    ))}
                </div>

                <h1>{text.title}</h1>
                <p className="demo-subtitle">{text.subtitle}</p>

                {remaining === 0 ? (
                    <div className="demo-state demo-state-error" role="alert">
                        <strong>{text.limitReached}</strong>
                        <span>{text.limit}</span>
                    </div>
                ) : step === "consent" ? (
                    <>
                        <h2>{text.consentTitle}</h2>
                        <p>{text.consentBody}</p>
                        <label className="demo-consent">
                            <input
                                type="checkbox"
                                checked={consented}
                                onChange={(event) => setConsented(event.target.checked)}
                            />
                            <span>{text.consentCheck}</span>
                        </label>
                        <button className="demo-primary" disabled={!consented} onClick={() => setStep("profile")}>
                            {text.continue}
                        </button>
                    </>
                ) : step === "profile" ? (
                    <>
                        <h2>{text.profileTitle}</h2>
                        <label className="demo-label" htmlFor="demo-nickname">{text.nickname}</label>
                        <input
                            id="demo-nickname"
                            className="demo-input"
                            value={nickname}
                            maxLength={30}
                            autoComplete="nickname"
                            placeholder={text.nicknamePlaceholder}
                            onChange={(event) => setNickname(event.target.value)}
                        />
                        <fieldset className="demo-language">
                            <legend>{text.language}</legend>
                            <button className={language === "th" ? "active" : ""} onClick={() => chooseLanguage("th")}>
                                {text.thai}
                            </button>
                            <button className={language === "en" ? "active" : ""} onClick={() => chooseLanguage("en")}>
                                {text.english}
                            </button>
                        </fieldset>
                        <div className="demo-actions">
                            <button className="demo-secondary" onClick={() => setStep("consent")}>{text.back}</button>
                            <button
                                className="demo-primary"
                                disabled={!nickname.trim()}
                                onClick={() => setStep("permissions")}
                            >
                                {text.continue}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <h2>{text.permissionTitle}</h2>
                        <p>{text.permissionBody}</p>
                        {statusMessage && (
                            <div
                                className={`demo-state ${permissionState === "ready" ? "demo-state-ready" : permissionState !== "checking" ? "demo-state-error" : ""}`}
                                role="status"
                            >
                                {permissionState === "checking" && <CircularProgress size={22} />}
                                <span>{statusMessage}</span>
                            </div>
                        )}
                        <button
                            className="demo-permission"
                            disabled={permissionState === "checking" || isLaunching}
                            onClick={checkPermissions}
                        >
                            {permissionState === "denied" || permissionState === "missing"
                                ? text.retry
                                : text.checkPermission}
                        </button>
                        {launchError && <div className="demo-state demo-state-error" role="alert">{launchError}</div>}
                        <small>{text.remaining(remaining)} · {text.limit}</small>
                        <div className="demo-actions">
                            <button className="demo-secondary" disabled={isLaunching} onClick={() => setStep("profile")}>
                                {text.back}
                            </button>
                            <button
                                className="demo-primary"
                                disabled={permissionState !== "ready" || isLaunching}
                                onClick={launchDemo}
                            >
                                {isLaunching && <CircularProgress size={18} color="inherit" />}
                                {isLaunching ? text.launching : text.launch}
                            </button>
                        </div>
                    </>
                )}
            </section>
        </main>
    );
};

export default DemoPage;
